import { createElement, useMemo, forwardRef } from 'react';
import { CheckboxProps } from './';
import { BaseHelpPopup } from '../HelpPopup/__internal__/';
import { Checkmark } from '@sas/react-svg';
import { useDesignTokens, renderDesignTokens, DesignTokens } from '../../theme';
import { postfixTestId } from '../../util/testId';
import { Label } from '../Label';
import { Text } from '../Text';
import { Icon } from '../Icon';
import { createUseHtmlId } from '../../hooks/useHtmlId';
import { setAlpha } from '../../design-tokens';
import cx from 'classnames';
import cn from './Checkbox.css.json';
import './Checkbox.css';

const useHtmlId = createUseHtmlId('Checkbox');

export const UnthemedCheckbox = forwardRef<HTMLDivElement, CheckboxProps>(function UnthemedCheckbox(
  props,
  ref
) {
  const descriptionId = useHtmlId();
  const dt = useDesignTokens();

  // line-height patch specific to checkbox to help keep checkbox and its label centered
  // NOTE: this will break if the unit is not px
  const labelFontSizeInPixels =
    parseFloat(dt.enableNovaTokens ? dt.fontNova.size.m : dt.font.size.m) * 16;
  const labelHeight = Math.round(
    labelFontSizeInPixels * (dt.enableNovaTokens ? dt.fontNova.lineHeight : dt.font.lineHeight)
  );
  // the 14 is the height of the checkbox in pixels. Only define the custom margin if a label is supplied.
  const marginTopCalc = props.label ? `${Math.floor((labelHeight - 14) / 2)}px` : undefined;
  return (
    <div
      className={cx(cn.container, !props.label && cn.noLabel, props.className)}
      style={props.style}
      data-testid={props.testId}
    >
      <div
        ref={ref}
        role="checkbox"
        data-testid={postfixTestId(props.testId, 'checkbox')}
        aria-checked={props.checked}
        className={cx(cn.main, props.disabled && cn.disabled, props.readOnly && cn.readOnly)}
        aria-disabled={props.disabled}
        aria-readonly={props.readOnly}
        aria-required={props.required}
        aria-describedby={props.description ? descriptionId : undefined}
        aria-labelledby={props.ariaLabelledBy}
        aria-label={props.ariaLabel}
        onClick={(e) => {
          if (!props.disabled && !props.readOnly && !e.isDefaultPrevented()) {
            e.preventDefault();
            props.onPress?.(e);
          }
        }}
        tabIndex={props.focusable === false ? -1 : 0} // Setting to -1 so that when focusable changes from true to false, focus is not lost
        onKeyDown={(e) => {
          if (!props.disabled && !props.readOnly && !e.isDefaultPrevented() && e.key === ' ') {
            e.preventDefault();
            props.onPress?.(e);
          }
        }}
      >
        {
          // Render the label first so a screen reader will announce the label before the tooltip.
          props.label && (
            <Label
              testId={postfixTestId(props.testId, 'label')}
              {...props.labelProps}
              className={cx(cn.label, props.labelProps?.className)}
              theme={() => {
                return {
                  '--label-text-cursor': 'inherit',
                  '--label-fg': 'var(--checkbox-label-fg)',
                };
              }}
              disabled={props.disabled}
              wrapText={props.wrapText}
              required={props.required && !props.readOnly}
            >
              {props.label}
            </Label>
          )
        }
        <span
          className={cx(cn.box, {
            [cn.mixed]: props.checked === 'mixed',
            [cn.selected]: props.checked === true,
          })}
          title={props.tooltip}
          style={{ marginTop: marginTopCalc }}
        >
          {props.checked === true ? (
            <Icon
              icon={Checkmark}
              tokens={() => ({ fg: 'var(--checkbox-icon-fg)' })}
              tooltip=""
              ariaLabel=""
            />
          ) : null}
        </span>
      </div>
      {props.helpPopup ? (
        <BaseHelpPopup
          className={cn.helpButton}
          {...props.helpPopup}
          additionalAriaText={props.label ?? props.ariaLabel}
        />
      ) : null}
      {props.description && (
        <Text
          id={descriptionId}
          testId={postfixTestId(props.testId, 'description')}
          className={cn.description}
          disabled={props.disabled}
          wrapText={props.wrapDescription}
          tokens={() => ({ fg: 'var(--checkbox-description-fg)' })}
        >
          {props.description}
        </Text>
      )}
    </div>
  );
});

export const Checkbox = forwardRef<HTMLDivElement, CheckboxProps>(function Checkbox(allProps, ref) {
  const { style: userStyle, ...props } = allProps;

  const dt = useDesignTokens();
  const themeStyle = useMemo(
    () => ({
      ...getCheckboxDesignTokens(dt, {
        disabled: props.disabled,
        readOnly: props.readOnly,
        description: props.description,
      }),
      ...props.theme?.(dt),
    }),
    [dt, props]
  );

  return <UnthemedCheckbox {...props} style={{ ...themeStyle, ...userStyle }} ref={ref} />;
});

export const getCheckboxDesignTokens = (
  dt: DesignTokens,
  opts?: { disabled?: boolean; readOnly?: boolean; description?: string }
) => {
  const tokens = dt.stateSecondaryToggleNova;
  const disabled = opts?.disabled;
  const readOnly = opts?.readOnly;
  const description = opts?.description;

  /*
   * List sets these tokens at the root of the List for performance considerations.
   * Without knowledge of the state of each checkbox in the list, it needs a separate token
   * for each state.
   */
  return {
    ...renderDesignTokens(
      {
        'spacing-s': dt.spacing.core.s,
        'spacing-xs': dt.spacing.core.xs,
        'spacing-xxs': dt.spacing.core.xxs,
        'font-size-s': dt.fontNova.size.s,
        'font-size-m': dt.fontNova.size.m,
        'squish-xxs': dt.spacing.insetSquish.xxs.padding,
        'row-gap': description ? dt.spacing.core.xs : 0,
        'label-fg': disabled ? tokens.disabled.fg : dt.c.colorTextDefault,
        'description-fg': disabled ? tokens.disabled.fg : dt.c.colorTextLabel,
        'icon-fg':
          disabled && readOnly
            ? tokens.disabled.fg
            : disabled
            ? tokens.disabledSelected.fg
            : readOnly
            ? tokens.readOnly.fg
            : tokens.selected.fg,
        ...tokens.normal,
      },
      'checkbox'
    ),
    ...renderDesignTokens(tokens.selected, 'checkbox-selected'),
    ...renderDesignTokens(tokens.disabled, 'checkbox-disabled'),
    ...renderDesignTokens(tokens.disabledSelected, 'checkbox-disabled-selected'),
    ...renderDesignTokens(tokens.readOnly, 'checkbox-read-only'),

    ...(!dt.enableNovaTokens && getCheckboxLegacyDesignTokens(dt, opts)),
  };
};

export const getCheckboxLegacyDesignTokens = (
  dt: DesignTokens,
  opts?: { disabled?: boolean; readOnly?: boolean; description?: string }
) => {
  const tokens = dt.stateSecondaryToggle;

  let readOnlyTokens = { fg: dt.c.n_3, bg: dt.c.white, bd: dt.c.n2 };
  let descriptionTextColor = dt.c.n_1;
  switch (dt.themeVariant) {
    case 'dark':
      readOnlyTokens = { fg: dt.c.white, bg: dt.c.p_3, bd: setAlpha(dt.c.white, 0.2) };
      descriptionTextColor = setAlpha(dt.c.white, 0.8);
      break;
    case 'high-contrast':
      readOnlyTokens = { fg: dt.c.white, bg: dt.c.black, bd: dt.c.white };
      descriptionTextColor = dt.c.white;
      break;
  }

  return {
    ...renderDesignTokens(
      {
        'font-size-s': dt.font.size.s,
        'font-size-m': dt.font.size.m,
        'label-fg': opts?.disabled ? tokens.disabled.fg : dt.c.n_3,
        'description-fg': opts?.disabled ? tokens.disabled.fg : descriptionTextColor,
        ...tokens.normal,
        'icon-fg':
          opts?.disabled && opts?.readOnly
            ? tokens.disabled.fg
            : opts?.disabled
            ? tokens.disabledSelected.fg
            : opts?.readOnly
            ? readOnlyTokens.fg
            : tokens.selected.fg,
      },
      'checkbox'
    ),
    ...renderDesignTokens(tokens.selected, 'checkbox-selected'),
    ...renderDesignTokens(tokens.disabled, 'checkbox-disabled'),
    ...renderDesignTokens(tokens.disabledSelected, 'checkbox-disabled-selected'),
    ...renderDesignTokens(readOnlyTokens, 'checkbox-read-only'),
  };
};

/**
 * @deprecated use `getCheckboxDesignTokens()` instead
 */
export const getCheckboxThemeTokens = (
  dt: DesignTokens,
  state?: { disabled?: boolean; readOnly?: boolean; description?: string }
) => getCheckboxDesignTokens(dt, state);
